/**
 * DostPolishTextButton (Enhancement 4).
 *
 * Small "Ask Dost to polish" button that wraps any admin content-block
 * textarea (or RichTextEditor value). Opens a compact modal with:
 *   - tone dropdown
 *   - side-by-side "original vs polished" preview
 *   - Accept / Regenerate / Cancel
 *
 * Props:
 *   value       - the current text (plain or HTML string)
 *   onChange    - (newText) => void; called when the user accepts a polish
 *   blockType   - optional string, forwarded to Dost so it can bias tone
 *   label       - button label (default: "Ask Dost to polish")
 *   size        - "sm" | "xs" (default "sm")
 *   disabled    - boolean
 *   isHtml      - preserve HTML formatting in the diff view (default false)
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Sparkles, RefreshCcw, Check, X } from 'lucide-react';
import api from '@/utils/api';
import { usePixels } from '@/contexts/PixelContext';

const TONES = [
  { value: 'warm', label: 'Warm' },
  { value: 'confident', label: 'Confident' },
  { value: 'casual', label: 'Casual' },
  { value: 'thoughtful', label: 'Thoughtful' },
  { value: 'playful', label: 'Playful' },
  { value: 'concise', label: 'Concise' },
];

const DostPolishTextButton = ({
  value,
  onChange,
  blockType = null,
  label = 'Ask Dost to polish',
  size = 'sm',
  disabled = false,
  isHtml = false,
  // External-control mode (used by Dost Co-pilot "Fix with Dost"):
  // when `externalOpen` is a boolean, the component skips rendering the
  // trigger button and treats itself as a compare dialog whose lifecycle
  // is owned by the parent. `initialProposed` pre-fills the compare view.
  externalOpen = null,
  onExternalClose = null,
  initialProposed = null,
  initialMeta = null,
  titleOverride = null,
  regenerateEndpoint = null,     // e.g. '/dost/admin/apply-critique'
  regeneratePayload = null,       // extra fields sent on Try again
}) => {
  const isExternal = typeof externalOpen === 'boolean';
  const [open, setOpen] = useState(false);
  const dialogOpen = isExternal ? externalOpen : open;
  const setDialogOpen = (v) => {
    if (isExternal) {
      if (!v) onExternalClose?.();
    } else {
      setOpen(v);
    }
  };
  const [tone, setTone] = useState('warm');
  const [phase, setPhase] = useState(isExternal ? 'compare' : 'tone'); // tone | loading | compare | error | quota
  const [polished, setPolished] = useState(initialProposed || '');
  const [meta, setMeta] = useState(initialMeta || { latency_ms: 0, tokens_used: 0 });
  const [errMsg, setErrMsg] = useState('');
  const [quota, setQuota] = useState({ used_today: 0, limit: 40, remaining: 40 });

  const originalText = (value || '').toString();
  const hasText = originalText.trim().length > 0;
  const { trackEvent } = usePixels();

  const fetchQuota = async () => {
    try {
      const { data } = await api.get('/dost/quota?feature=admin_polish_text');
      setQuota(data);
    } catch (_) { /* non-fatal */ }
  };

  useEffect(() => {
    if (dialogOpen) {
      if (isExternal) {
        setPhase('compare');
        setPolished(initialProposed || '');
        setMeta(initialMeta || { latency_ms: 0, tokens_used: 0 });
        setErrMsg('');
      } else {
        setPhase('tone');
        setPolished('');
        setErrMsg('');
        fetchQuota();
      }
    }
    // eslint-disable-next-line
  }, [dialogOpen, initialProposed]);

  const doPolish = async () => {
    setPhase('loading');
    setErrMsg('');
    // Bundle 167 · hoist endpoint + body out of try so the catch
    // block's retry path can reach them (they were block-scoped
    // constants in the try; the catch reference was a real ReferenceError
    // waiting to fire on any 5xx retry).
    const endpoint = isExternal && regenerateEndpoint
      ? regenerateEndpoint
      : '/dost/admin/polish-text';
    const body = isExternal && regeneratePayload
      ? regeneratePayload
      : { text: (value || '').toString(), tone, block_type: blockType };
    try {
      const { data } = await api.post(endpoint, body);
      const outText = data.polished_text || data.proposed_text || '';
      setPolished(outText);
      setMeta({
        latency_ms: data.latency_ms || 0,
        tokens_used: data.tokens_used || 0,
        // Bug X2: expose the backend's no-op signal so the compare UI can
        // clearly label "already polished" instead of a fake diff.
        no_change: !!data.no_change,
        similarity: data.similarity,
      });
      setPhase('compare');
      if (!isExternal) fetchQuota();
      trackEvent('Use Dosti Polish', {
        feature: 'polish_with_dost',
        surface: blockType ? `admin_block_${blockType}` : 'admin_polish_text',
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      const isNetwork = !status || err?.code === 'ERR_NETWORK';
      if (status === 429) {
        setPhase('quota');
      } else {
        // Auto-retry once on 5xx / network / upstream timeouts before giving
        // up - the underlying LLM call is upstream-flaky and a single retry
        // clears most transient failures without a user click.
        if ((isNetwork || (status && status >= 500)) && !err.__retried) {
          try {
            await new Promise((r) => setTimeout(r, 800));
            const { data } = await api.post(endpoint, body);
            const outText2 = data.polished_text || data.proposed_text || '';
            setPolished(outText2);
            setMeta({
              latency_ms: data.latency_ms || 0,
              tokens_used: data.tokens_used || 0,
              no_change: !!data.no_change,
              similarity: data.similarity,
            });
            setPhase('compare');
            if (!isExternal) fetchQuota();
            trackEvent('Use Dosti Polish', {
              feature: 'polish_with_dost',
              surface: blockType ? `admin_block_${blockType}` : 'admin_polish_text',
            });
            return;
          } catch (err2) {
            err2.__retried = true;
            const detail2 = err2?.response?.data?.detail || '';
            const status2 = err2?.response?.status;
            const msg = detail2
              || (status2 ? `Dost service returned ${status2}. Try again shortly.` : 'Network hiccup reaching Dost. Try again.');
            setErrMsg(msg);
            setPhase('error');
            return;
          }
        }
        // Non-retryable: show the server-provided detail verbatim so the
        // user sees the real cause (rate limit, upstream 502, etc.).
        const msg = detail
          || (status ? `Dost service returned ${status}. Try again shortly.` : 'Dost is having a moment. Try again shortly.');
        setErrMsg(msg);
        setPhase('error');
      }
    }
  };

  const accept = () => {
    onChange?.(polished);
    setDialogOpen(false);
  };

  return (
    <>
      {!isExternal && (
        <Button
          type="button"
          variant="outline"
          size={size}
          onClick={() => setDialogOpen(true)}
          disabled={disabled || !hasText}
          className="border-primary-300 text-primary-800 hover:bg-primary-50"
          data-testid="dost-polish-text-btn"
          title={hasText ? 'Ask Dost to polish this text' : 'Write something first, then Dost can polish it'}
        >
          <Sparkles className="h-3.5 w-3.5 mr-1.5" />
          {label}
        </Button>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl" data-testid="dost-polish-text-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary-700" />
              {titleOverride || 'Polish with Dost'}
              {blockType && (
                <Badge variant="outline" className="ml-2 text-[10px] uppercase tracking-widest">
                  {blockType.replace(/_/g, ' ')}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {phase === 'tone' && (
            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-secondary-700 mb-1">Tone</div>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="w-48" data-testid="polish-tone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-secondary-700 mb-1">Your text</div>
                <div className="rounded-lg border border-neutral-200 p-3 max-h-40 overflow-auto text-sm text-neutral-800 bg-neutral-50">
                  {isHtml ? (
                    <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: originalText }} />
                  ) : (
                    <div className="whitespace-pre-wrap">{originalText}</div>
                  )}
                </div>
              </div>

              <div className="text-[11px] text-neutral-500">
                Dost keeps your facts. It won&apos;t invent new venue details, prices, or names.
                {' '}Today: {quota.used_today}/{quota.limit} polishes used.
              </div>
            </div>
          )}

          {phase === 'loading' && (
            <div className="py-10 text-center text-sm text-neutral-600">
              <Sparkles className="h-5 w-5 mx-auto animate-pulse text-primary-700" />
              Polishing…
            </div>
          )}

          {phase === 'compare' && (
            <>
              {meta.no_change && (
                <div
                  className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs"
                  data-testid="polish-no-change-hint"
                >
                  <strong>Dost thinks this is already polished.</strong> Try adding more detail
                  or rephrasing manually, or use <em>Try again</em> for a different angle.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Original</div>
                  <div className="rounded-lg border border-neutral-200 p-3 max-h-72 overflow-auto text-sm bg-neutral-50">
                    {isHtml ? (
                      <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: originalText }} />
                    ) : (
                      <div className="whitespace-pre-wrap">{originalText}</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-primary-700 mb-1">
                    {meta.no_change ? 'Polished by Dost (no meaningful change)' : 'Polished by Dost'}
                  </div>
                  <div
                    className={`rounded-lg border-2 p-3 max-h-72 overflow-auto text-sm ${meta.no_change ? 'border-amber-200 bg-amber-50/40' : 'border-primary-300 bg-primary-50/40'}`}
                    data-testid="polish-result"
                  >
                    {isHtml ? (
                      <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: polished }} />
                    ) : (
                      <div className="whitespace-pre-wrap">{polished}</div>
                    )}
                  </div>
                  <div className="mt-1 text-[11px] text-neutral-500">
                    {meta.latency_ms}ms · {meta.tokens_used} tokens
                    {typeof meta.similarity === 'number' ? ` · similarity ${(meta.similarity * 100).toFixed(0)}%` : ''}
                  </div>
                </div>
              </div>
            </>
          )}

          {phase === 'error' && (
            <div className="p-4 rounded-lg bg-error-50 text-error-800 text-sm" data-testid="polish-error">
              {errMsg}
            </div>
          )}

          {phase === 'quota' && (
            <div className="p-4 rounded-lg bg-warning-50 text-warning-800 text-sm">
              You&apos;ve used all your polishes for today. Comes back tomorrow.
            </div>
          )}

          <DialogFooter className="gap-2">
            {phase === 'tone' && (
              <>
                <Button variant="ghost" onClick={() => setDialogOpen(false)} data-testid="polish-cancel">
                  <X className="h-4 w-4 mr-1.5" /> Cancel
                </Button>
                <Button
                  onClick={doPolish}
                  className="bg-primary-800 hover:bg-primary-900 text-white"
                  disabled={!hasText || quota.remaining === 0}
                  data-testid="polish-run"
                >
                  <Sparkles className="h-4 w-4 mr-1.5" /> Polish
                </Button>
              </>
            )}
            {phase === 'compare' && (
              <>
                <Button variant="ghost" onClick={() => setDialogOpen(false)} data-testid="polish-discard">
                  <X className="h-4 w-4 mr-1.5" /> Discard
                </Button>
                <Button variant="outline" onClick={doPolish} data-testid="polish-regenerate">
                  <RefreshCcw className="h-4 w-4 mr-1.5" /> Try again
                </Button>
                <Button
                  onClick={accept}
                  className={meta.no_change
                    ? 'bg-neutral-200 text-neutral-500 hover:bg-neutral-200 cursor-not-allowed'
                    : 'bg-primary-800 hover:bg-primary-900 text-white'}
                  disabled={meta.no_change}
                  data-testid="polish-accept"
                  title={meta.no_change ? 'No meaningful changes - nothing to apply' : 'Replace the field with the polished version'}
                >
                  <Check className="h-4 w-4 mr-1.5" /> {meta.no_change ? 'No changes' : 'Use this'}
                </Button>
              </>
            )}
            {(phase === 'error' || phase === 'quota') && (
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DostPolishTextButton;
